import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Clock,
  Calendar,
  User as UserIcon,
  Timer,
  MoreHorizontal,
  Plus,
  Flag,
  DollarSign,
  Hash,
  Paperclip,
  Activity,
  Upload,
  Circle,
  PlayCircle,
  AlertCircle,
  CheckCircle2,
  Edit3,
  Archive,
  Trash2,
  MessageSquare,
  FileText,
  Image
} from 'lucide-react';
import { PageHeader, Button } from '../../common';
import MDEditor from '@uiw/react-md-editor';
import { Task, User } from '../../../types';
import { useAuth } from '../../../hooks';
import api from '../../../services/api';
import { ImageUpload } from '../../annotations/ImageUpload';
import { ImageAnnotatorCanvas } from '../../annotations/ImageAnnotatorCanvas';
import { FigmaEmbedViewer, FigmaFileSync } from '../../figma';
import { API_BASE_URL } from '../../../services/api';
import './MarkdownEditor.css';

interface TaskFile {
  id: number;
  filename: string;
  original_name: string;
  file_size: number;
  file_type: string;
  image_width: number | null;
  image_height: number | null;
  is_image: boolean;
  description: string;
  created_at: string;
  first_name: string;
  last_name: string;
  file_path?: string;
  s3Url?: string;
}

interface TaskDetailProps {
  task: Task;
  onBack: () => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: number) => void;
  users: User[];
}

export const TaskDetail: React.FC<TaskDetailProps> = ({
  task,
  onBack,
  onEdit,
  onDelete,
  users
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedTask, setEditedTask] = useState<Task>(task);
  const [activeTab, setActiveTab] = useState<'overview' | 'files' | 'activity' | 'subtasks' | 'figma'>('overview');
  const [imageFiles, setImageFiles] = useState<TaskFile[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectData, setProjectData] = useState<any>(null);
  const [figmaFileId, setFigmaFileId] = useState<number | null>(null);

  // Load task details only when task ID changes
  useEffect(() => {
    loadTaskDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  // Update editedTask when task prop changes, but not if we're actively editing
  useEffect(() => {
    if (!editingField) {
      console.log('Syncing editedTask from task prop:', task);
      // Normalize task - handle both snake_case and camelCase from API
      const normalizedTask = {
        ...task,
        assignedTo: task.assignedTo || (task as any).assigned_to,
        dueDate: task.dueDate || (task as any).due_date,
        estimatedHours: task.estimatedHours || (task as any).estimated_hours
      };
      console.log('Task assignedTo:', normalizedTask.assignedTo, 'Task dueDate:', normalizedTask.dueDate);
      setEditedTask(normalizedTask);
    }
  }, [task, editingField]);

  const loadTaskDetails = useCallback(async () => {
    if (!task.id || !token) return;

    try {
      setLoading(true);
      setImageError(null);

      console.log('=== LOADING TASK DETAILS ===');
      console.log('Task ID:', task.id);
      console.log('Project ID:', task.projectId || (task as any).project_id);

      const projectId = task.projectId || (task as any).project_id;

      if (!projectId) {
        console.error('No project ID found for task:', task);
        setLoading(false);
        return;
      }

      const [timeResponse, filesResponse, projectResponse, figmaResponse, commentsResponse] = await Promise.all([
        api.get<any>(`/api/projects/${projectId}/time-entries`, token),
        api.get<any>(`/api/files/task/${task.id}`, token),
        api.get<any>(`/api/projects/${projectId}`, token),
        api.get<any>(`/api/tasks/${task.id}/figma`, token).catch(() => ({ figmaFile: null })),
        api.get<any>(`/api/projects/tasks/${task.id}/comments`, token).catch(() => ({ comments: [] }))
      ]);

      console.log('Files response:', filesResponse);
      console.log('Figma response:', figmaResponse);
      console.log('Comments response:', commentsResponse);

      setTimeEntries(Array.isArray(timeResponse) ? timeResponse : []);
      setProjectData(projectResponse || null);
      setComments(commentsResponse?.comments || []);

      // Set Figma file ID if exists
      if (figmaResponse?.figmaFile?.id) {
        setFigmaFileId(figmaResponse.figmaFile.id);
      } else {
        setFigmaFileId(null);
      }

      // Process files response
      let filesArray = [];
      if (filesResponse && filesResponse.files && Array.isArray(filesResponse.files)) {
        filesArray = filesResponse.files;
      } else if (Array.isArray(filesResponse)) {
        filesArray = filesResponse;
      }

      console.log('Processed files array:', filesArray);
      console.log('Setting files state with:', filesArray.length, 'files');

      setFiles(filesArray);

      // Update image files for the Files tab
      const imageFiles = filesArray.filter((file: any) => file.is_image);
      console.log('Image files:', imageFiles);

      // Debug: Log the file URLs being generated
      imageFiles.forEach((file: TaskFile) => {
        const url = `${API_BASE_URL}/api/files/serve/${file.filename}`;
        console.log(`File ${file.filename} URL:`, url);
      });

      setImageFiles(imageFiles);
    } catch (error) {
      console.error('Failed to load task details:', error);
      setImageError('Failed to load task files. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [task.id, task.projectId, token]);

  const handleFieldUpdate = async (field: string, value: any) => {
    try {
      setLoading(true);

      // Get old value for activity logging
      const oldValue = (editedTask as any)[field];

      // Format the value properly for the API
      let formattedValue = value;
      if (field === 'dueDate' && value) {
        // For due date, just use the date string as-is (YYYY-MM-DD format)
        // Don't convert to full ISO string as it adds timezone complications
        formattedValue = value;
      }

      // Map camelCase field names to snake_case for API
      const fieldMap: Record<string, string> = {
        assignedTo: 'assigned_to',
        dueDate: 'due_date',
        estimatedHours: 'estimated_hours'
      };

      const apiFieldName = fieldMap[field] || field;

      // Send complete task data with the updated field to prevent data loss
      const updateData: Record<string, any> = {
        title: editedTask.title || editedTask.name,
        description: editedTask.description,
        status: editedTask.status,
        priority: editedTask.priority,
        assigned_to: field === 'assignedTo' ? formattedValue : editedTask.assignedTo,
        due_date: field === 'dueDate' ? formattedValue : editedTask.dueDate,
        estimated_hours: field === 'estimatedHours' ? formattedValue : editedTask.estimatedHours
      };

      // Override the specific field being updated with correct API field name
      if (!['assignedTo', 'dueDate', 'estimatedHours'].includes(field)) {
        updateData[apiFieldName] = formattedValue;
      }

      console.log('Sending update to API:', updateData);
      const apiResponse = await api.put(`/api/projects/tasks/${task.id}`, updateData, token || undefined);
      console.log('API Response:', apiResponse);

      // Log the activity change
      await logActivity(field, oldValue, formattedValue);

      // Update local task state for immediate UI feedback
      const updated = { ...editedTask, [field]: formattedValue };
      console.log('Updated editedTask:', updated);
      setEditedTask(updated);

      // Update the parent component's task data to persist changes
      onEdit(updated);

      // Exit edit mode after successful save
      setEditingField(null);
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      alert(`Failed to update ${field}. Please try again.`);
      setEditedTask(task);
    } finally {
      setLoading(false);
    }
  };

  const logActivity = async (field: string, oldValue: any, newValue: any) => {
    try {
      // Format the activity message based on field type
      let activityMessage = '';
      const fieldDisplayName = field === 'assignedTo' ? 'assignee' :
                               field === 'dueDate' ? 'due date' : field;

      if (field === 'assignedTo') {
        const oldUser = oldValue ? users.find(u => u.id === oldValue) : null;
        const newUser = newValue ? users.find(u => u.id === newValue) : null;
        const oldUserName = oldUser ? `${oldUser.first_name} ${oldUser.last_name}` : 'Unassigned';
        const newUserName = newUser ? `${newUser.first_name} ${newUser.last_name}` : 'Unassigned';
        activityMessage = `Changed ${fieldDisplayName} from "${oldUserName}" to "${newUserName}"`;
      } else if (field === 'dueDate') {
        const oldDate = oldValue ? new Date(oldValue).toLocaleDateString() : 'No due date';
        const newDate = newValue ? new Date(newValue).toLocaleDateString() : 'No due date';
        activityMessage = `Changed ${fieldDisplayName} from "${oldDate}" to "${newDate}"`;
      } else {
        const oldVal = oldValue || 'None';
        const newVal = newValue || 'None';
        activityMessage = `Changed ${fieldDisplayName} from "${oldVal}" to "${newVal}"`;
      }

      // Create activity entry (for now, add to comments array - in real app would be separate endpoint)
      const newActivity = {
        id: Date.now(),
        content: activityMessage,
        type: 'system',
        created_at: new Date().toISOString(),
        user: {
          first_name: 'System',
          last_name: ''
        }
      };

      setComments(prev => [newActivity, ...prev]);

    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };

  const generateTaskId = (taskId: number) => {
    return `TSK-${String(taskId).padStart(5, '0')}`;
  };

  // Image handling functions
  const handleImageUploadSuccess = (file: any) => {
    console.log('=== UPLOAD SUCCESS ===');
    console.log('Uploaded file:', file);
    setImageError(null);
    console.log('Calling loadTaskDetails to refresh...');
    loadTaskDetails(); // Reload to get the new image
  };

  const handleImageUploadError = (errorMessage: string) => {
    setImageError(errorMessage);
  };

  const handleAddComment = async () => {
    if (!comment.trim() || !token) return;

    try {
      const response = await api.post<any>(
        `/api/projects/tasks/${task.id}/comments`,
        { comment: comment.trim() },
        token
      );

      if (response?.comment) {
        setComments(prev => [...prev, response.comment]);
        setComment('');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!token) return;

    try {
      await api.delete<any>(
        `/api/projects/tasks/${task.id}/comments/${commentId}`,
        token
      );

      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'todo': return <Circle className="w-3 h-3" />;
      case 'in_progress': return <PlayCircle className="w-3 h-3" />;
      case 'review': return <AlertCircle className="w-3 h-3" />;
      case 'completed': return <CheckCircle2 className="w-3 h-3" />;
      default: return <Circle className="w-3 h-3" />;
    }
  };

  const getStatusStyles = (status: string) => {
    switch(status) {
      case 'todo': return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'in_progress': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'review': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getPriorityStyles = (priority: string) => {
    switch(priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-red-50 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'low': return 'bg-green-50 text-green-700 border-green-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const assignedUser = users.find(u => u.id === editedTask.assignedTo);
  const timeTracked = timeEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
  const estimate = editedTask.estimatedHours || 0;
  const progress = estimate > 0 ? Math.min((timeTracked / estimate) * 100, 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={task.title || 'Untitled Task'}
        subtitle={`Task ID: ${generateTaskId(task.id)}`}
        breadcrumb={
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tasks
          </button>
        }
        actions={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusStyles(editedTask.status)}`}>
                {getStatusIcon(editedTask.status)}
                {editedTask.status.replace('_', ' ')}
              </span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityStyles(editedTask.priority || 'medium')}`}>
                {editedTask.priority || 'medium'}
              </span>
            </div>
            <div className="w-px h-6 bg-gray-300"></div>
            <Button
              variant="secondary"
              size="sm"
              icon={<Edit3 className="w-3.5 h-3.5" />}
            >
              Edit
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<Archive className="w-3.5 h-3.5" />}
            >
              Archive
            </Button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        }
      />

      <div className="px-6 py-6">
        {/* Task Info Card */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <UserIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Assignee:</span>
                <div
                  className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center cursor-pointer hover:bg-indigo-200"
                  title={assignedUser ? `${assignedUser.first_name} ${assignedUser.last_name}` : 'Unassigned'}
                >
                  <span className="text-xs font-medium text-indigo-700">
                    {assignedUser ? `${assignedUser.first_name?.[0] || ''}${assignedUser.last_name?.[0] || ''}` : '?'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Due:</span>
                <span className="text-sm font-medium text-gray-900">
                  {editedTask.dueDate ? new Date(editedTask.dueDate).toLocaleDateString() : 'No due date'}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Timer className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Time:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {timeTracked}h / {estimate}h
                  </span>
                  <div className="w-20 bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${
                        progress > 100 ? 'bg-red-500' :
                        progress > 75 ? 'bg-amber-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Billable</span>
              </div>
            </div>

            <Button
              variant="primary"
              size="sm"
              icon={<Timer className="w-4 h-4" />}
            >
              Start Timer
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div className="flex gap-6 px-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('files')}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'files'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Images & Annotations
              {imageFiles.length > 0 && (
                <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">
                  {imageFiles.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'activity'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Activity
              <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">
                {comments.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('subtasks')}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'subtasks'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              Subtasks
            </button>
            <button
              onClick={() => setActiveTab('figma')}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'figma'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" viewBox="0 0 38 57" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 28.5C19 23.8056 22.8056 20 27.5 20C32.1944 20 36 23.8056 36 28.5C36 33.1944 32.1944 37 27.5 37C22.8056 37 19 33.1944 19 28.5Z" fill="#1ABCFE"/>
                <path d="M0 46.5C0 41.8056 3.80558 38 8.5 38H19V46.5C19 51.1944 15.1944 55 10.5 55C5.80558 55 0 51.1944 0 46.5Z" fill="#0ACF83"/>
                <path d="M19 0V19H27.5C32.1944 19 36 15.1944 36 10.5C36 5.80558 32.1944 0 27.5 0H19Z" fill="#FF7262"/>
                <path d="M0 10.5C0 15.1944 3.80558 19 8.5 19H19V0H8.5C3.80558 0 0 5.80558 0 10.5Z" fill="#F24E1E"/>
                <path d="M0 28.5C0 33.1944 3.80558 37 8.5 37H19V20H8.5C3.80558 20 0 23.8056 0 28.5Z" fill="#A259FF"/>
              </svg>
              Figma
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex gap-6">
            {/* Main Content */}
            <div className="flex-1 bg-white border border-gray-200 rounded-lg p-6">
              {activeTab === 'overview' && (
                <div className="max-w-4xl">
                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Progress</p>
                          <p className="text-lg font-semibold text-gray-900">{Math.round(progress)}%</p>
                        </div>
                        <Activity className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Time Remaining</p>
                          <p className="text-lg font-semibold text-gray-900">{Math.max(estimate - timeTracked, 0)}h</p>
                        </div>
                        <Clock className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Comments</p>
                          <p className="text-lg font-semibold text-gray-900">{comments.length}</p>
                        </div>
                        <MessageSquare className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Files</p>
                          <p className="text-lg font-semibold text-gray-900">{files.length}</p>
                        </div>
                        <Paperclip className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Description</h3>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      {editingField === 'description' ? (
                        <div className="markdown-editor-container border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                          <MDEditor
                            value={editedTask.description || ''}
                            onChange={(val) => setEditedTask(prev => ({ ...prev, description: val || '' }))}
                            preview="edit"
                            hideToolbar={false}
                            toolbarHeight={50}
                            textareaProps={{
                              placeholder: 'Add a description... Use markdown for formatting: **bold**, *italic*, [links](url), lists, etc.',
                              onKeyDown: (e) => {
                                if (e.key === 'Escape') {
                                  setEditingField(null);
                                  setEditedTask(prev => ({ ...prev, description: task.description }));
                                }
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                  handleFieldUpdate('description', editedTask.description);
                                }
                              },
                              autoFocus: true,
                              style: {
                                fontSize: '14px',
                                lineHeight: '1.5',
                                fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                border: 'none',
                                resize: 'vertical',
                                minHeight: '200px'
                              }
                            }}
                            height={280}
                            data-color-mode="light"
                          />
                          <div className="bg-slate-50 px-4 py-3 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                              <div className="text-sm text-gray-600 flex items-center gap-4">
                                <span className="flex items-center gap-1">
                                  <span>💡</span>
                                  <strong>Ctrl+Enter</strong> to save • <strong>Esc</strong> to cancel
                                </span>
                                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                  Supports Markdown formatting
                                </span>
                              </div>
                              <div className="flex gap-3">
                                <button
                                  onClick={() => {
                                    setEditingField(null);
                                    setEditedTask(prev => ({ ...prev, description: task.description }));
                                  }}
                                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-300"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleFieldUpdate('description', editedTask.description)}
                                  className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors font-medium shadow-sm"
                                >
                                  Save Description
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="cursor-pointer hover:bg-gray-100 rounded-lg p-4 border border-transparent hover:border-gray-200 min-h-[3rem] transition-all duration-150 group"
                          onClick={() => setEditingField('description')}
                          title="Click to edit description (Rich text editor with Markdown support)"
                        >
                          {editedTask.description ? (
                            <div className="text-gray-700 prose prose-sm max-w-none">
                              <MDEditor.Markdown
                                source={editedTask.description}
                                style={{ backgroundColor: 'transparent', color: 'inherit' }}
                              />
                            </div>
                          ) : (
                            <p className="text-gray-400 italic flex items-center gap-2 group-hover:text-gray-600">
                              <span>✏️</span>
                              Add a description... (Rich text editor with formatting support)
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Task Details</h3>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Created By</span>
                          <span className="text-sm font-medium text-gray-900">System</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Created On</span>
                          <span className="text-sm font-medium text-gray-900">
                            {task.createdAt ? new Date(task.createdAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Last Updated</span>
                          <span className="text-sm font-medium text-gray-900">
                            {task.updatedAt ? new Date(task.updatedAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Project Info</h3>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Project</span>
                          <span className="text-sm font-medium text-gray-900">
                            {projectData?.name || 'Loading...'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Client</span>
                          <span className="text-sm font-medium text-gray-900">
                            {projectData?.client?.name || projectData?.clientName || 'Loading...'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Project Status</span>
                          <span className={`text-sm font-medium ${
                            projectData?.status === 'active' ? 'text-blue-600' :
                            projectData?.status === 'completed' ? 'text-green-600' :
                            projectData?.status === 'on-hold' ? 'text-orange-600' :
                            projectData?.status === 'cancelled' ? 'text-red-600' :
                            'text-gray-600'
                          }`}>
                            {projectData?.status ? projectData.status.charAt(0).toUpperCase() + projectData.status.slice(1).replace('-', ' ') : 'Loading...'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comments Section */}
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Comments ({comments.length})
                    </h3>

                    {/* Comment Input */}
                    <div className="mb-6">
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <textarea
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder="Add a comment..."
                          className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={3}
                        />
                        <div className="flex justify-end mt-3">
                          <button
                            onClick={handleAddComment}
                            disabled={!comment.trim()}
                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            Add Comment
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Comments List */}
                    <div className="space-y-4">
                      {comments.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No comments yet. Be the first to comment!</p>
                        </div>
                      ) : (
                        comments.map((item) => {
                          const getUserColor = (userId: number) => {
                            const colors = [
                              { bg: 'bg-blue-100', text: 'text-blue-700' },
                              { bg: 'bg-green-100', text: 'text-green-700' },
                              { bg: 'bg-purple-100', text: 'text-purple-700' },
                              { bg: 'bg-pink-100', text: 'text-pink-700' },
                              { bg: 'bg-indigo-100', text: 'text-indigo-700' },
                              { bg: 'bg-orange-100', text: 'text-orange-700' },
                            ];
                            return colors[userId % colors.length];
                          };

                          const color = getUserColor(item.user_id);
                          const initials = `${item.first_name?.[0] || ''}${item.last_name?.[0] || ''}`;

                          return (
                            <div key={item.id} className="flex gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${color.bg}`}>
                                <span className={`text-xs font-medium ${color.text}`}>
                                  {initials}
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="rounded-lg border bg-white border-gray-200 p-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-gray-900 text-sm">
                                        {item.first_name} {item.last_name}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {new Date(item.created_at).toLocaleString()}
                                      </span>
                                    </div>
                                    {item.user_id === users.find(u => u.email === localStorage.getItem('userEmail'))?.id && (
                                      <button
                                        onClick={() => handleDeleteComment(item.id)}
                                        className="text-gray-400 hover:text-red-600 text-xs"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.comment}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'files' && (
                <div className="space-y-6">
                  {/* Upload Section */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Images for Annotation</h3>

                    {imageError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                        {imageError}
                      </div>
                    )}

                    <ImageUpload
                      taskId={task.id}
                      onUploadSuccess={handleImageUploadSuccess}
                      onError={handleImageUploadError}
                    />
                  </div>

                  {/* Image Annotator Canvas */}
                  <ImageAnnotatorCanvas
                    files={files.filter(f => f.is_image).map(f => ({
                      id: f.id,
                      filename: f.filename,
                      original_name: f.original_name,
                      file_url: `${API_BASE_URL}/api/files/serve/${f.filename}`,
                      file_type: f.file_type,
                      file_size: f.file_size,
                      image_width: f.image_width,
                      image_height: f.image_height,
                      uploaded_at: f.uploaded_at
                    }))}
                    taskId={task.id}
                    projectId={task.projectId}
                    users={users}
                    onAnnotationUpdate={loadTaskDetails}
                  />
                </div>
              )}

              {activeTab === 'activity' && (
                <div>
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Activity className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-900 mb-1">Activity Log</h4>
                        <p className="text-sm text-blue-700">
                          This is an audit trail of all changes made to this task. System activities are automatically logged.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Activity Timeline - Only show system activities */}
                  <div className="space-y-4">
                    {/* For now, show placeholder since we don't have audit log implemented yet */}
                    <div className="text-center py-8 text-gray-400">
                      <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="font-medium mb-1">Audit Log Coming Soon</p>
                      <p className="text-sm">All task changes will be automatically tracked here</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'subtasks' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">Subtasks</h3>
                    <button className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Add Subtask
                    </button>
                  </div>

                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center">
                    <CheckCircle2 className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600">No subtasks yet</p>
                    <p className="text-xs text-gray-500 mt-1">Break down this task into smaller pieces</p>
                  </div>
                </div>
              )}

              {activeTab === 'figma' && token && (
                <div>
                  {figmaFileId ? (
                    <FigmaEmbedViewer
                      fileId={figmaFileId}
                      token={token}
                      compact={false}
                    />
                  ) : (
                    <FigmaFileSync
                      clientId={projectData?.client_id || projectData?.clientId || 0}
                      projectId={task.projectId || projectData?.id || 0}
                      token={token}
                      onFileLinked={async (file) => {
                        try {
                          await api.post(`/api/figma/files/${file.id}/link-task`, { taskId: task.id }, token);
                          setFigmaFileId(file.id);
                        } catch (error) {
                          console.error('Failed to link Figma file:', error);
                          alert('Failed to link Figma file to task');
                        }
                      }}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Right Properties Panel */}
            <div className="w-80 bg-white border border-gray-200 rounded-lg p-4">
              {/* Task Properties */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Properties</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Status</label>
                    <select
                      value={task.status}
                      onChange={(e) => handleFieldUpdate('status', e.target.value)}
                      className="w-full text-sm px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="review">Review</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Priority</label>
                    <select
                      value={task.priority}
                      onChange={(e) => handleFieldUpdate('priority', e.target.value)}
                      className="w-full text-sm px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Assignee</label>
                    <select
                      value={editedTask.assignedTo || ''}
                      onChange={(e) => handleFieldUpdate('assignedTo', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full text-sm px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Due Date</label>
                    <input
                      type="date"
                      value={editedTask.dueDate ? new Date(editedTask.dueDate).toISOString().split('T')[0] : ''}
                      onChange={(e) => handleFieldUpdate('dueDate', e.target.value || null)}
                      className="w-full text-sm px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Estimate</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={editedTask.estimatedHours || ''}
                        onChange={(e) => handleFieldUpdate('estimatedHours', e.target.value ? parseInt(e.target.value) : null)}
                        className="flex-1 text-sm px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600 flex items-center px-2">hours</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">Delete Task</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this task? This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 px-4 py-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onDelete(task.id);
                    setShowDeleteModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetail;